import { Fragment } from "react";
import { motion } from "framer-motion";
import { Check, Volume2, VolumeX } from "lucide-react";
import { useCms } from "@/lib/cms";
import { resolveAboutVideoUrl, useAboutSectionVideo } from "@/hooks/useAboutSectionVideo";
import { parseCompanyAboutCopy } from "@/components/sections/about/aboutContent";
import TechStackStrip from "@/components/sections/company-v2/TechStackStrip";
import "./AboutSection.css";

const ITEMS = [
  "기업 홈페이지",
  "웹 플랫폼",
  "SaaS(Software as a Service)",
  "관리자 시스템",
  "AI 자동화 시스템",
  "데이터 기반 서비스",
];

export default function AboutSection() {
  const { cms } = useCms();
  const aboutVideoSrc = resolveAboutVideoUrl(cms.media_about_video);
  const { videoRef, wrapRef, videoMuted, videoFailed, toggleVideoSound, onVideoError } =
    useAboutSectionVideo(aboutVideoSrc);
  const { titleLines, bodyText } = parseCompanyAboutCopy(cms.about_title, cms.about_body);

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-white text-slate-900 py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6"
          >
            <div className="text-xs font-semibold tracking-[0.25em] uppercase text-[#0A2540]">
              Who We Are — 01
            </div>
            <h2
              data-testid="about-page-title"
              className="font-display font-kr mt-5 text-4xl md:text-[2.75rem] lg:text-[3.25rem] font-bold tracking-tight leading-[1.15] [word-break:keep-all]"
            >
              {titleLines.map((line, index) => {
                const isMuted = titleLines.length >= 2 && index === 1;
                const isLastNowrap = titleLines.length >= 3 && index === titleLines.length - 1;
                return (
                  <Fragment key={`${index}-${line}`}>
                    {index > 0 ? <br /> : null}
                    {isMuted ? (
                      <span className="text-slate-400">{line}</span>
                    ) : isLastNowrap ? (
                      <span className="inline-block whitespace-nowrap">{line}</span>
                    ) : (
                      line
                    )}
                  </Fragment>
                );
              })}
            </h2>

            <p
              data-testid="about-page-body"
              className="font-kr mt-8 text-base md:text-lg text-slate-600 leading-relaxed max-w-xl whitespace-pre-line"
            >
              {bodyText}
            </p>

            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
              {ITEMS.map((it, i) => (
                <motion.li
                  key={it}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  data-testid={`about-item-${i}`}
                  className="flex items-center gap-3 text-[15px] text-slate-800"
                >
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#0A2540] text-white">
                    <Check size={13} strokeWidth={2.5} />
                  </span>
                  <span className="font-medium">{it}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            ref={wrapRef}
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-6 relative"
          >
            <div className="relative aspect-[4/5] sm:aspect-[5/6] lg:aspect-[4/5] rounded-3xl overflow-hidden border border-slate-200 shadow-[0_30px_60px_-30px_rgba(10,37,64,0.25)] bg-slate-100">
              {videoFailed ? (
                <>
                  <div className="ds-about-video-fallback" aria-hidden="true" />
                  <p className="ds-about-video-fallback__hint font-kr">
                    영상 파일을 불러오지 못했습니다.
                    <br />
                    관리자 → 미디어 → 「회사소개 페이지 영상」에 MP4를 업로드해 주세요.
                  </p>
                </>
              ) : (
                <video
                  ref={videoRef}
                  key={aboutVideoSrc}
                  src={aboutVideoSrc}
                  className="absolute inset-0 w-full h-full object-cover"
                  autoPlay
                  loop
                  muted={videoMuted}
                  playsInline
                  preload="auto"
                  onError={onVideoError}
                  aria-label="DSITESOLUTION 팀 협업 영상"
                />
              )}
              {!videoFailed ? (
                <button
                  type="button"
                  data-testid="about-video-sound-toggle"
                  aria-label={videoMuted ? "영상 소리 켜기" : "영상 소리 끄기"}
                  aria-pressed={!videoMuted}
                  onClick={toggleVideoSound}
                  className="absolute top-4 right-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-black/55 text-white backdrop-blur-sm transition hover:border-white/40 hover:bg-black/75"
                >
                  {videoMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                </button>
              ) : null}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/40 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between pointer-events-none">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/80">Since 2025</div>
                  <div className="font-display text-white text-xl font-semibold mt-1">DSITESOLUTION</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-sm md:text-base font-semibold text-white tracking-wide">
                    www.dsitesolution.com
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <TechStackStrip />
      </div>
    </section>
  );
}
